import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, NumberArray, Object3D } from '@wonderlandengine/api';
import { Utilities } from '../utilities/utilities.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';
import { Configuration, AudioOptions } from '../data/configuration.ts';
import { ColourDescription } from './components/colour-description.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

type FadableMaterial = Material;
type FadableKey = {
	[TK in keyof FadableMaterial]: FadableMaterial[TK] extends NumberArray ? TK : never;
}[keyof FadableMaterial];

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;

/**
 * Accessories Configurator:
 */
export class AccessoriesConfigurator extends Component {
	static TypeName = 'accessories-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	tunnelPocket!: Object3D;

	@property.object({ required: true })
	exhaust!: Object3D;

	@property.object({ required: true })
	sportsExhaust!: Object3D;

	@property.object({ required: true })
	sportsExhaustDescriptions!: Object3D;

	@property.object({ required: true })
	cupHolder!: Object3D;

	@property.object({ required: true })
	connectivityPack!: Object3D;

	@property.object({ required: true })
	luggageRack!: Object3D;

	@property.object({ required: true })
	luggageRackBlack!: Object3D;

	@property.object({ required: true })
	bluetoothAudio!: Object3D;

	@property.object({ required: true })
	sennheiserAudio!: Object3D;
	
	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _exhaust!: MeshComponent;
	private _sportsExhaust!: MeshComponent;
	private _sportsExhaustDescriptions!: ColourDescription[];
	private _cupHolder!: MeshComponent;
	private _connectivityPack!: MeshComponent;
	private _luggageRack!: MeshComponent;
	private _luggageRackBlack!: MeshComponent;
	private _bluetoothAudio!: MeshComponent;
	private _sennheiserAudio!: MeshComponent;

	private _tweenManager = new TweenManager();

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start(): void {

		this._luggageRack = this.luggageRack.getComponent(MeshComponent)!;
		this._luggageRackBlack = this.luggageRackBlack.getComponent(MeshComponent)!;
		this._exhaust = this.exhaust.getComponent(MeshComponent)!;
		this._sportsExhaust = this.sportsExhaust.getComponent(MeshComponent)!;
		this._sportsExhaustDescriptions = this.sportsExhaustDescriptions.getComponents(ColourDescription)!;
		this._cupHolder = this.cupHolder.getComponent(MeshComponent)!;
		this._connectivityPack = this.connectivityPack.getComponent(MeshComponent)!;
		this._bluetoothAudio = this.bluetoothAudio.getComponent(MeshComponent)!;
		this._sennheiserAudio = this.sennheiserAudio.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	// remove all accessories from model.
	resetAccessories() {
		Utilities.setActiveAll(this.tunnelPocket, false);
		this._cupHolder.active = false;
		this._connectivityPack.active = false;
		this._sportsExhaust.active = false;
		this._exhaust.active = true;
		this._luggageRack.active = false;
		this._luggageRackBlack.active = false;
	}

	selectStorageAccessory(id: string) {

		const isActive = Configuration.accessoryStorageOptions.has(id);

		// optionally toggle the tunnel pocket visibility.
		if (id === Configuration.tunnelStorageOption) {
			Utilities.setActiveAll(this.tunnelPocket, isActive);
		}
	}
	
	selectEquipmentAccessory(id: string) {

		const isActive = Configuration.accessoryEquipmentOptions.has(id);

		if (id === Configuration.cupHolderOption) {
			this._cupHolder.active = isActive;
		}

		if (id === Configuration.connectivityPackOption) {
			this._connectivityPack.active = isActive;
		}

		// if either of the sports exhaust items are chosen, de-select the other, as only one can be selected.
		else if (id === Configuration.silverSportsExhaustOption || id === Configuration.blackSportsExhaustOption) {
			this._sportsExhaust.active = isActive;
			this._exhaust.active = !isActive;

			if (isActive) {
				const sportsExhaustDescription = this._sportsExhaustDescriptions.find(element => element.colourId === id);

				if (!sportsExhaustDescription) {
					console.error('No sports exhaust description found for id: ', id);
					return;
				}

				this._fadeColour(this._sportsExhaust, sportsExhaustDescription.colour, 'albedoColor' as FadableKey);
			}
		}
	}

	selectAftermarketAccessory(id: string) {

		const isActive = Configuration.accessoryAftermarketOptions.has(id);

		// disable all racks.
		this._luggageRack.active = false;
		this._luggageRackBlack.active = false;

		// do nothing if item was deselected.
		if (!isActive) return;

		// set which luggage rack is active based on id.
		if (id === Configuration.blackLuggageRackOption) {
			this._luggageRackBlack.active = true;
		} else {
			this._luggageRack.active = true;
		}
	}

	selectAudioAccessory(id: string) {
		const sennheiserSelected = id === AudioOptions.Sennheiser;

		this._bluetoothAudio.active = !sennheiserSelected;
		this._sennheiserAudio.active = sennheiserSelected;
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Smoothly fades the colour of any material on the given mesh between the current colour and the given colour, using a tween animation.
	*/
	private _fadeColour(
		mesh: MeshComponent,
		targetColour: NumberArray,
		targetProperty: FadableKey
	) {
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		const material = mesh.material.clone() as FadableMaterial;
		mesh.material = material;

		const prop = targetProperty as string;

		const colourTween = new Tween({
			[prop]: material[targetProperty],
		});
		colourTween
			.to(
				{
					[prop]: targetColour
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				(material as Record<FadableKey, NumberArray>)[targetProperty] = c[prop];
			})
			.easing(Easing.Quadratic.InOut);

		this._tweenManager.start(colourTween);
	}
}

